import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Rectangle,
} from "recharts";

import OfficeRiskFilter from "../../filters/OfficeRiskFilter";

import { useOfficeRiskFilter } from "../../api/getOfficeRiskFilter";

import type {
  CustomTooltipProps,
  CustomXAxisTickProps,
  LicensesOfficeChartProps,
  OfficeRiskChartItem,
  OfficeRiskSelectOption,
  ResponsiveChartSettings,
} from "../../types";

import type { BLPOfficeRiskDto } from "../../api/getBLPDashboard";

const COLOR_MISSING = "#1E6FBA";
const COLOR_UPCOMING = "#DB2777";

const DEFAULT_OFFICE_COUNT = 10;
const MAX_SELECTED_OFFICES = 15;

const getResponsiveChartSettings = (
  containerWidth: number
): ResponsiveChartSettings => {
  if (containerWidth >= 1400) {
    return {
      barSize: 62,
      barCategoryGap: "5%",
      chartHeight: 235,
      chartTopMargin: 12,
      xAxisHeight: 72,
      tickFontSize: 11,
      tickLineHeight: 15,
      maximumCharactersPerLine: 15,
      maximumLines: 4,
      yAxisFontSize: 12,
      tooltipFontSize: 13,
    };
  }

  if (containerWidth >= 1100) {
    return {
      barSize: 54,
      barCategoryGap: "6%",
      chartHeight: 225,
      chartTopMargin: 10,
      xAxisHeight: 70,
      tickFontSize: 11,
      tickLineHeight: 15,
      maximumCharactersPerLine: 13,
      maximumLines: 4,
      yAxisFontSize: 11,
      tooltipFontSize: 12,
    };
  }

  if (containerWidth >= 850) {
    return {
      barSize: 46,
      barCategoryGap: "7%",
      chartHeight: 215,
      chartTopMargin: 10,
      xAxisHeight: 68,
      tickFontSize: 10,
      tickLineHeight: 14,
      maximumCharactersPerLine: 12,
      maximumLines: 4,
      yAxisFontSize: 11,
      tooltipFontSize: 12,
    };
  }

  if (containerWidth >= 650) {
    return {
      barSize: 38,
      barCategoryGap: "9%",
      chartHeight: 205,
      chartTopMargin: 8,
      xAxisHeight: 66,
      tickFontSize: 9,
      tickLineHeight: 13,
      maximumCharactersPerLine: 10,
      maximumLines: 4,
      yAxisFontSize: 10,
      tooltipFontSize: 11,
    };
  }

  return {
    barSize: 30,
    barCategoryGap: "11%",
    chartHeight: 195,
    chartTopMargin: 8,
    xAxisHeight: 62,
    tickFontSize: 8,
    tickLineHeight: 12,
    maximumCharactersPerLine: 8,
    maximumLines: 4,
    yAxisFontSize: 9,
    tooltipFontSize: 10,
  };
};

const getAdaptiveBarSize = (
  defaultBarSize: number,
  containerWidth: number,
  officeCount: number
): number => {
  if (officeCount === 0 || officeCount > 5) {
    return defaultBarSize;
  }

  const reservedHorizontalSpace = 90;

  const availableChartWidth = Math.max(
    containerWidth - reservedHorizontalSpace,
    0
  );

  const availableWidthPerOffice = availableChartWidth / officeCount;

  const calculatedBarSize = Math.round(availableWidthPerOffice * 0.48);

  return Math.max(defaultBarSize, Math.min(calculatedBarSize, 125));
};

const useResponsiveChartSettings = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [containerWidth, setContainerWidth] = useState(700);

  const [settings, setSettings] = useState<ResponsiveChartSettings>(
    getResponsiveChartSettings(700)
  );

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const updateSettings = (width?: number) => {
      const currentContainerWidth = width ?? container.clientWidth;

      setContainerWidth(currentContainerWidth);

      setSettings(getResponsiveChartSettings(currentContainerWidth));
    };

    updateSettings();

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];

        if (!entry) {
          return;
        }

        updateSettings(entry.contentRect.width);
      });

      resizeObserver.observe(container);

      return () => {
        resizeObserver.disconnect();
      };
    }

    const handleWindowResize = () => {
      updateSettings();
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  return {
    containerRef,
    containerWidth,
    settings,
  };
};

const splitLabelIntoLines = (
  value: string,
  maximumCharactersPerLine = 12,
  maximumLines = 4
): string[] => {
  const words = value.trim().split(/\s+/);

  const lines: string[] = [];

  let currentLine = "";

  words.forEach((word) => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word;

    if (nextLine.length <= maximumCharactersPerLine) {
      currentLine = nextLine;

      return;
    }

    if (currentLine) {
      lines.push(currentLine);

      currentLine = "";
    }

    if (word.length > maximumCharactersPerLine) {
      let remainingWord = word;

      while (remainingWord.length > maximumCharactersPerLine) {
        lines.push(remainingWord.slice(0, maximumCharactersPerLine));

        remainingWord = remainingWord.slice(maximumCharactersPerLine);
      }

      currentLine = remainingWord;

      return;
    }

    currentLine = word;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  if (lines.length <= maximumLines) {
    return lines;
  }

  const visibleLines = lines.slice(0, maximumLines);

  const lastLineIndex = maximumLines - 1;

  const lastLine = visibleLines[lastLineIndex];

  visibleLines[lastLineIndex] =
    lastLine.length > maximumCharactersPerLine - 3
      ? `${lastLine.slice(0, maximumCharactersPerLine - 3)}...`
      : `${lastLine}...`;

  return visibleLines;
};

const CustomXAxisTick: React.FC<CustomXAxisTickProps> = ({
  x = 0,
  y = 0,
  payload,
  fontSize = 10,
  lineHeight = 14,
  maximumCharactersPerLine = 12,
  maximumLines = 4,
}) => {
  const fullLabel = String(payload?.value ?? "");

  const lines = splitLabelIntoLines(
    fullLabel,
    maximumCharactersPerLine,
    maximumLines
  );

  return (
    <g transform={`translate(${x},${y})`}>
      <title>{fullLabel}</title>

      {lines.map((line, index) => (
        <text
          key={`${fullLabel}-${index}`}
          x={0}
          y={0}
          dy={11 + index * lineHeight}
          textAnchor="middle"
          fill="#6B7280"
          fontSize={fontSize}
          fontFamily="'Segoe UI', system-ui, sans-serif"
        >
          {line}
        </text>
      ))}
    </g>
  );
};

const CustomTooltip = ({
  active,
  payload,
  fontSize = 12,
}: CustomTooltipProps): React.ReactElement | null => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const item = payload[0]?.payload;

  if (!item) {
    return null;
  }

  return (
    <div
      className="pointer-events-none min-w-[170px] rounded-md border border-blue-100 bg-white px-3 py-2 text-left shadow-md"
      style={{
        fontSize,
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}
    >
      <p className="mb-1 font-bold text-[#1E3A6E]">{item.officeName}</p>

      <p className="text-black">
        {item.missingLicenses.toLocaleString()}{" "}
        <span
          style={{
            color: COLOR_MISSING,
          }}
        >
          Missing Licenses
        </span>
      </p>

      <p className="text-black">
        {item.upcomingRenewals.toLocaleString()}{" "}
        <span
          style={{
            color: COLOR_UPCOMING,
          }}
        >
          Upcoming Renewals
        </span>
      </p>

      <p className="mt-1 border-t border-blue-100 pt-1 text-black">
        {item.totalRisk.toLocaleString()}{" "}
        <span className="text-gray-600">Total Risk</span>
      </p>
    </div>
  );
};

const CustomLegend = () => (
  <div className="flex items-center justify-center gap-x-4 font-sans text-[11px] text-gray-700 2xl:gap-x-5 2xl:text-xs">
    <div className="flex items-center gap-1.5 whitespace-nowrap">
      <span
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
        style={{
          backgroundColor: COLOR_MISSING,
        }}
      />

      <span>Missing Licenses</span>
    </div>

    <div className="flex items-center gap-1.5 whitespace-nowrap">
      <span
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
        style={{
          backgroundColor: COLOR_UPCOMING,
        }}
      />

      <span>Upcoming Renewals</span>
    </div>
  </div>
);

const MissingLicensesBarShape = (props: any): React.ReactElement => {
  const upcomingRenewals = Number(props?.payload?.upcomingRenewals ?? 0);

  const radius = upcomingRenewals > 0 ? [0, 0, 0, 0] : [5, 5, 0, 0];

  return <Rectangle {...props} radius={radius} />;
};

const LicensesOfficeChart: React.FC<LicensesOfficeChartProps> = ({
  data,
  isLoading = false,
  isError = false,
  onAppliedOfficeIdsChange,
}) => {
  const [selectedOfficeOptions, setSelectedOfficeOptions] = useState<
    OfficeRiskSelectOption[]
  >([]);

  const [appliedOfficeIds, setAppliedOfficeIds] = useState<number[]>([]);

  const [filteredOfficeRiskData, setFilteredOfficeRiskData] = useState<
    BLPOfficeRiskDto[] | null
  >(null);

  const {
    mutate: loadOfficeRisk,
    isLoading: isOfficeRiskUpdating,
    isError: isOfficeRiskUpdateError,
  } = useOfficeRiskFilter();

  const { containerRef, containerWidth, settings } =
    useResponsiveChartSettings();

  const {
    barSize,
    barCategoryGap,
    chartHeight,
    chartTopMargin,
    xAxisHeight,
    tickFontSize,
    tickLineHeight,
    maximumCharactersPerLine,
    maximumLines,
    yAxisFontSize,
    tooltipFontSize,
  } = settings;

  const handleOfficeSelectionChange = useCallback(
    (options: OfficeRiskSelectOption[]) => {
      setSelectedOfficeOptions(options.slice(0, MAX_SELECTED_OFFICES));
    },
    []
  );

  const handleOfficeSearch = useCallback(() => {
    if (isOfficeRiskUpdating) {
      return;
    }

    const officeIds = Array.from(
      new Set(
        selectedOfficeOptions
          .map((option) => Number(option.value))
          .filter((officeId) => Number.isFinite(officeId) && officeId > 0)
      )
    ).slice(0, MAX_SELECTED_OFFICES);

    loadOfficeRisk(
      {
        officeIds,
        top: officeIds.length > 0 ? officeIds.length : DEFAULT_OFFICE_COUNT,
      },
      {
        onSuccess: (response) => {
          setAppliedOfficeIds(officeIds);

          setFilteredOfficeRiskData(response);

          onAppliedOfficeIdsChange?.(officeIds);
        },
      }
    );
  }, [
    isOfficeRiskUpdating,
    loadOfficeRisk,
    onAppliedOfficeIdsChange,
    selectedOfficeOptions,
  ]);
  const handleOfficeRefresh = useCallback(() => {
    setSelectedOfficeOptions([]);

    setAppliedOfficeIds([]);

    setFilteredOfficeRiskData(null);

    onAppliedOfficeIdsChange?.([]);
  }, [onAppliedOfficeIdsChange]);

  const chartData = useMemo<OfficeRiskChartItem[]>(() => {
    const sourceData = filteredOfficeRiskData ?? data ?? [];

    return sourceData
      .map((item) => {
        const officeId = Number(item.officeId);

        const missingLicenses = Number(item.missingLicenses ?? 0);

        const upcomingRenewals = Number(item.upcomingRenewals ?? 0);

        return {
          officeId,

          officeName: item.officeName?.trim() || "Unknown Office",

          missingLicenses,

          upcomingRenewals,

          totalRisk: Number(
            item.totalRisk ?? missingLicenses + upcomingRenewals
          ),
        };
      })
      .filter((item) => Number.isFinite(item.officeId) && item.officeId > 0)
      .sort((first, second) => {
        if (second.totalRisk !== first.totalRisk) {
          return second.totalRisk - first.totalRisk;
        }

        if (second.missingLicenses !== first.missingLicenses) {
          return second.missingLicenses - first.missingLicenses;
        }

        if (second.upcomingRenewals !== first.upcomingRenewals) {
          return second.upcomingRenewals - first.upcomingRenewals;
        }

        return first.officeName.localeCompare(second.officeName);
      })
      .slice(
        0,
        appliedOfficeIds.length > 0
          ? MAX_SELECTED_OFFICES
          : DEFAULT_OFFICE_COUNT
      );
  }, [filteredOfficeRiskData, data, appliedOfficeIds.length]);

  const adaptiveBarSize = useMemo(() => {
    return getAdaptiveBarSize(barSize, containerWidth, chartData.length);
  }, [barSize, containerWidth, chartData.length]);

  const hasChartData = chartData.length > 0;

  const isInitialLoading = isLoading && !hasChartData;

  const isInitialError = isError && !hasChartData;

  let content: React.ReactNode;

  if (isInitialLoading) {
    content = (
      <div
        className="flex items-center justify-center font-sans text-xs text-gray-500"
        style={{
          height: chartHeight,
        }}
      >
        Loading...
      </div>
    );
  } else if (isInitialError) {
    content = (
      <div
        className="flex items-center justify-center font-sans text-xs text-red-600"
        style={{
          height: chartHeight,
        }}
      >
        Unable to load office risk data.
      </div>
    );
  } else if (hasChartData) {
    content = (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={chartData}
          margin={{
            top: chartTopMargin,
            right: 12,
            left: 0,
            bottom: 3,
          }}
          barCategoryGap={barCategoryGap}
          barGap={0}
          barSize={adaptiveBarSize}
        >
          <CartesianGrid vertical={false} stroke="#E5E7EB" />

          <XAxis
            dataKey="officeName"
            tick={
              <CustomXAxisTick
                fontSize={tickFontSize}
                lineHeight={tickLineHeight}
                maximumCharactersPerLine={maximumCharactersPerLine}
                maximumLines={maximumLines}
              />
            }
            axisLine={false}
            tickLine={false}
            interval={0}
            height={xAxisHeight}
            minTickGap={0}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            tick={{
              fontSize: yAxisFontSize,
              fill: "#6B7280",
              fontFamily: "'Segoe UI', system-ui, sans-serif",
            }}
            tickFormatter={(value: number) => value.toLocaleString()}
            width={yAxisFontSize >= 11 ? 48 : 40}
          />

          <Tooltip
            content={<CustomTooltip fontSize={tooltipFontSize} />}
            cursor={false}
          />

          <Bar
            dataKey="missingLicenses"
            stackId="officeRisk"
            fill={COLOR_MISSING}
            name="Missing Licenses"
            shape={MissingLicensesBarShape}
          />

          <Bar
            dataKey="upcomingRenewals"
            stackId="officeRisk"
            fill={COLOR_UPCOMING}
            name="Upcoming Renewals"
            radius={[5, 5, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  } else {
    content = (
      <div
        className="flex items-center justify-center px-4 text-center font-sans text-xs text-gray-400"
        style={{
          height: chartHeight,
        }}
      >
        {appliedOfficeIds.length > 0
          ? "No Office Risk data is available for the selected offices."
          : "No Office Risk data available."}
      </div>
    );
  }

  const showLegend = !isInitialLoading && !isInitialError && hasChartData;

  return (
    <div className="relative flex h-full flex-col overflow-visible rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="shrink-0 rounded-t-xl border-b border-blue-100 bg-blue-50 px-4 py-2">
        <h2 className="m-0 text-left font-sans text-sm font-bold text-primary 2xl:text-base">
          Office Risk: Missing Licenses &amp; Upcoming Renewals
        </h2>
      </div>
      <div
        ref={containerRef}
        className="flex min-h-0 flex-1 flex-col px-4 pb-2 pt-1"
      >
        <OfficeRiskFilter
          selectedOptions={selectedOfficeOptions}
          onChange={handleOfficeSelectionChange}
          onSearch={handleOfficeSearch}
          onRefresh={handleOfficeRefresh}
          maxSelectedOffices={MAX_SELECTED_OFFICES}
          isUpdateError={isOfficeRiskUpdateError}
        />

        <div className="flex min-h-0 flex-1 items-center">
          <div className="w-full">{content}</div>
        </div>

        {showLegend && (
          <div className="shrink-0 border-t border-gray-100 pt-2">
            <CustomLegend />
          </div>
        )}
      </div>
    </div>
  );
};

export default LicensesOfficeChart;
